import Vue from "vue";
import { resolveRequestError } from "@/utils/auth";
import * as ENDPOINTS from "./endpoints";

export default class AuthService {
  /**
   *  Attempt to register
   * @param {Object} data
   * @param {String} data.firstname
   * @param {String} data.lastname
   * @param {String} data.email
   * @param {String} data.phonenumber
   * @param {String} data.password
   * @param {String} data.referrerscode
   *
   * @async
   * @throws will throw error if it is form validation error
   * @return {Object} returns user object
   */
  static async register({
    firstname,
    lastname,
    email,
    phonenumber,
    password,
    referrerscode
  }) {
    try {
      const { data } = await Vue.http.post(ENDPOINTS.REGISTER, {
        firstname,
        lastname,
        email,
        phonenumber,
        password,
        referrerscode
      });
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Attempt to login
   * @param {Object} data
   * @param {String} data.username
   * @param {String} data.password
   *
   * @async
   * @throws will throw error if it is form validation error
   * @return {Object} returns user object
   */
  static async login({ username, password }) {
    try {
      const { data } = await Vue.http.post(ENDPOINTS.LOGIN, {
        username,
        password
      });
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Change password
   * @param {Object} data
   * @param {String} data.oldpassword
   * @param {String} data.newpassword
   * @param {String} data.newpasswordconfirm
   *
   * @async
   * @return {Object} returns message
   * @throws form validation errors
   */
  static async changePassword({
    oldpassword,
    newpassword,
    newpasswordconfirm
  }) {
    try {
      const { data } = await Vue.http.put(ENDPOINTS.CHANGE_PASSWORD, {
        oldpassword,
        newpassword,
        newpasswordconfirm
      });
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Send verification code to phone number
   *
   * @async
   * @return {Object} returns message
   */
  static async sendVerificationPhoneNo() {
    try {
      const { data } = await Vue.http.get(ENDPOINTS.REQUEST_OTP("verifyphone"));
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  static async fetchReferrerViaCode(code) {
    try {
      const { data } = await Vue.http.get(
        ENDPOINTS.GET_REFERRER_VIA_CODE(code)
      );
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Verify phone number with code
   *
   * @async
   * @throws will throw error if it is form validation error
   * @return {Object} returns message
   */
  static async verifyPhoneNo(code) {
    try {
      const { data } = await Vue.http.put(ENDPOINTS.VERIFY_PHONENUMBER(code));
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Send reset password link to email
   * @param {String} email
   *
   * @async
   * @throws will throw error if it is form validation error
   * @return {Object} returns user object
   */
  static async forgotPassword(email) {
    try {
      const { data } = await Vue.http.post(ENDPOINTS.FORGOT_PASSWORD, {
        email
      });
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Attempt to reset password
   * @param {Object} resetData
   * @param {String} resetData.newpassword
   * @param {String} resetData.newpasswordconfirm
   * @param {String} resetData.token
   *
   * @async
   * @throws will throw error if it is form validation error
   * @return {Object}
   */
  static async resetPassword({ newpassword, newpasswordconfirm, token }) {
    try {
      const { data } = await Vue.http.post(ENDPOINTS.RESET_PASSWORD, {
        newpassword,
        newpasswordconfirm,
        token
      });
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   *  Attempt to verify email address via token
   * @param {String} token
   *
   * @async
   * @return {Object}
   */
  static async verifyEmail(token) {
    try {
      const { data } = await Vue.http.get(ENDPOINTS.VERIFY_EMAIL(token));
      return data;
    } catch (err) {
      resolveRequestError(err, true, false);
    }
  }

  /**
   *  Resend email verification
   *
   * @async
   * @return {Object} message
   *
   * @move this to user
   */
  static async resendEmailVerification() {
    try {
      const { data } = await Vue.http.get(ENDPOINTS.RESEND_EMAIL_VERIFICATION);
      return data;
    } catch (err) {
      resolveRequestError(err);
    }
  }

  /**
   * Set user status as logged in and
   * store photo_url
   *
   * @param {Object} user
   * @param {String} user.photourl
   * @todo this isn't still working. User profile isn't displayed immediately after login
   */
  static authenticateUser({ photourl }, token) {
    Vue.http.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("authToken", token);
    localStorage.setItem("UserStatus", btoa("true"));
    localStorage.setItem(
      "UserPhotoURL",
      btoa(
        photourl ||
          "http://www.buira.org/assets/images/shared/default-profile.png"
      )
    );
  }
}
